import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Rate } from '@core/models/Rate/Rate.model';
import { PrepaidRoomCluster } from '@core/models/PrepaidRoomCluster/PrepaidRoomCluster.model';
import { DisneyRoomCluster } from '@core/models/disney-room-cluster/DisneyRoomCluster.model';
import { Room } from '@core/models/Room/Room.model';
import { DISNEY_DINING_CARD_CODES, DISNEY_FREE_MEAL_PLAN_CODES } from '../../shared/constants/disney-offer-codes';
import { MealPlan } from '../../shared/enums/meal-plan.enum';
import { IDisneyClusterTickets } from '@core/models/modals';
import { ReqService } from '@core/services/req.service';
import { PriceService } from '@core/services/price.service';
import { SearchStateService } from '@core/services/search-state.service';

@Injectable()
export class DisneyService {
  constructor(
    private http: HttpClient,
    private readonly contextService: ReqService,
    private readonly priceService: PriceService,
    private readonly searchStateService: SearchStateService,
  ) {}

  /**
   * Gets the supplement of the required type or undefined if there is any
   * @param {Object} rate Where to get the supplement
   * @param {string} supplementType TICKET or MEAL
   * @return {Object | undefined}
   */
  public getRateSupplement(rate, supplementType) {
    supplementType = supplementType.toUpperCase();
    let supplement;
    if (rate.supplements) {
      supplement = rate.supplements.find(
        (supplementElement) => supplementElement.type === supplementType,
      );
    }
    if (supplement) {
      const cutText = supplement.description.indexOf('_');
      if (cutText > -1) {
        supplement.description = supplement.description.slice(0, cutText);
      }
    }
    return supplement;
  }

  /**
   * Returns true if the passed rate has a free meal plan
   * @param {Object} rate
   * @return {boolean}
   */
  public isMealPlanFree(rate) {
    const meal = this.getRateSupplement(rate, 'MEAL');
    let value = false;
    if (!meal) {
      return value;
    }
    if (meal.amount === 0) {
      value = true;
    }
    return value;
  }

  /** Set ticket days */
  public isTicketDays(days) {
    return (rate) => {
      if (
        this.getRateSupplement(rate, 'TICKET') &&
        this.getRateSupplement(rate, 'TICKET').days === days.toString()
      ) {
        return rate;
      }
    };
  }

  public firstDay(rateId: string, selectedTicket: string, date: Date,currencyCode:string): Observable<Rate> {
    return this.http.post<Rate>(`/hotels/async/disney/first-day`, {
      first_day_tickets: this.formatDate(date),
      rate_id: rateId,
      selected_ticket: selectedTicket,
      currency_code: currencyCode
    });
  }

  private formatDate(date: Date): string {
    let month = '' + (date.getMonth() + 1);
    let day = '' + date.getDate();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }

    return [date.getFullYear(), month, day].join('-');
  }

  public generateRoomClusters(clusters: PrepaidRoomCluster[]): DisneyRoomCluster[] {
    const namesRooms: Set<string> = new Set<string>();
    const disneyClusters = [];
    clusters.forEach((paidRoom: any) => {
      const parsedName = this.splitRoomName(
        paidRoom.rooms[0].room_description.description,
      );
      const room: Room = paidRoom.rooms[0];
      room.offerCode = parsedName.code ? parsedName.code.replace('-', '') : null;
      room.parsedName = parsedName.name;
      room.mealPlanName = parsedName.mealPlan;
      namesRooms.add(parsedName.name);
    });

    if (namesRooms.size > 0) {
      
      namesRooms.forEach((nameRoom) => {
        let specialOffer: any[] = [];


        const optionsRoom: any[] = clusters.filter((room: any) => {
          const nameActualRoom = this.splitRoomName(
            room.rooms[0].room_description.description,
          );

          if (
            nameActualRoom.name === nameRoom &&
            (DISNEY_DINING_CARD_CODES.includes(nameActualRoom.code.replace('-', '')) ||
              DISNEY_FREE_MEAL_PLAN_CODES.includes(nameActualRoom.code.replace('-', '')))
          ) {
            (room.rooms[0] as any).hasDiningCard = true;
            specialOffer.push(room);
            return false;
          }
          return nameActualRoom.name === nameRoom;
        });

        if (specialOffer.length > 0) {
          disneyClusters.push(this.createRoomOptions(specialOffer));
        }

        disneyClusters.push(this.createRoomOptions(optionsRoom));
      });
    }

    return disneyClusters;
  }

  private splitRoomName(nameRoom: string): {
    name: string;
    code: string;
    mealPlan: string;
  } {
    const offerCodeRegex = /-\d+[A-Z0-9]*C/g;
    const nameSplitted: string[] = nameRoom.split(':');
    let roomNameParsed = nameSplitted[0].trim();
    const codeRegexMatch = roomNameParsed.match(offerCodeRegex);
    const code = codeRegexMatch ? codeRegexMatch.shift() : null;

    if (code) {
      roomNameParsed = roomNameParsed.replace(code, '');
    }

    if (roomNameParsed.charAt(roomNameParsed.length - 1) !== '.') {
      roomNameParsed = roomNameParsed + '.';
    }
    return {
      name: roomNameParsed,
      mealPlan: nameSplitted[1] ? nameSplitted[1].trim() : null,
      code,
    };
  }

  private createRoomOptions(optionsRooms: any) {
    const roomOptions = {
      disney_meal_plans: [],
      rooms: null,
      offerCodes: [],
      rates: [],
      hasRoomOnlyOption: false,
      hasMealPlans: false,
      hasTickets: false,
      hasDiningCard: false
    };

    let offerCodes = [];
    optionsRooms.forEach((optionRoom) => {
      const room: Room = optionRoom.rooms[0];

      offerCodes.push(room.offerCode);

      if (roomOptions.rooms === null) {
        roomOptions.rooms = optionRoom.rooms;
      }

      let rates: Rate[];

      if (room.mealPlanName) {
        const nameMealPLan = room.mealPlanName.replace('"', '').replace(' ', ' ');

        rates = optionRoom.disney_meal_plans[0].rates;
        rates.forEach((rate) => {
          (rate.meal_plan as any).description = nameMealPLan;
        });
      } else {
        rates = optionRoom.disney_meal_plans[0].rates;
      }

      if ((roomOptions as any).rates) {
        roomOptions.rates = (roomOptions as any).rates.concat(rates);
      } else {
        roomOptions.rates = rates;
      }
    });
    offerCodes = offerCodes.filter((code) => code !== null);
    if (offerCodes.length > 0) {
      roomOptions.offerCodes = offerCodes;
    }
    roomOptions.hasRoomOnlyOption = roomOptions.rates.some(
      (rate) => rate.meal_plan.code === MealPlan.ROOM_ONLY,
    );
    roomOptions.hasTickets = roomOptions.rates.some(
      (rate) => rate.supplements && rate.supplements.length > 0,
    );
    roomOptions.hasMealPlans = roomOptions.rates.some(
      (rate) => rate.meal_plan.code !== MealPlan.ROOM_ONLY,
    );
    roomOptions.hasDiningCard = offerCodes.some(code =>
      DISNEY_DINING_CARD_CODES.includes(code) || DISNEY_FREE_MEAL_PLAN_CODES.includes(code)
    );
    return roomOptions;
  }

  public createTicketsCluster(cluster: PrepaidRoomCluster): IDisneyClusterTickets {
    const defaultRate = cluster.rates[0];
    return {
      preSelectedRate: defaultRate,
      clusterType: defaultRate.payment_method.code,
      selectedPlan: null,
      nameHotel: this.contextService.getHotelStaticInfo().name,
      meal_plan: null,
      nameRoom: cluster.rooms[0].parsedName,
      cluster: cluster as any,
      roomPricePerNight: this.priceService.getPrice(
        defaultRate,
        'NIGHT',
        this.searchStateService.getPriceBy() === 'TOTAL',
      ),
    };
  }
}
